import { useState } from 'react';
import RequestQuotePopup from '../../../components/feature/RequestQuotePopup';

const programmingServices = [
  {
    id: 1,
    title: 'Lutron Lighting Control',
    description: 'We provide specialized programming for Lutron\'s premier lighting and shading systems. Our expertise ensures that your HomeWorks QSX or RadioRA 3 system is not just installed, but masterfully configured to enhance your living space.\n\nFrom precise dimming curves to time-shifted occupancy logic, we create automated environments that respond intuitively to your lifestyle. We handle everything from keypad engraving coordination to complex conditional logic integration.',
    image: 'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&q=80&w=800',
    icon: 'ri-lightbulb-flash-line',
    tags: ['HomeWorks QSX', 'RadioRA 3', 'Athena', 'Shade Automation'],
    features: ['Custom Scene Logic', 'Time-Clock Scheduling', 'Occupancy/Vacancy Programming', 'Mobile App Configuration']
  },
  {
    id: 2,
    title: 'Savant Control Systems',
    description: 'Transform your home with a Savant system that is programmed for reliability and ease of use. We specialize in Savant Blueprint configuration, ensuring all your audio, video, lighting, and climate systems work together in perfect harmony.\n\nOur programming approach focuses on the end-user experience, creating customized layouts and "Savant Scenes" that allow you to control your entire home with a single tap. We ensure seamless integration of third-party devices and robust system stability.',
    image: 'https://images.unsplash.com/photo-1585314062340-f1a5a7c9328d?auto=format&fit=crop&q=80&w=800',
    icon: 'ri-command-line',
    tags: ['Savant Blueprint', 'Pro App Setup', 'AV Switching', 'Climate Integration'],
    features: ['Custom UI Layouts', 'Savant Scene Design', 'Third-Party Integration', 'TrueControl Configuration']
  }
];

export default function ProgrammingServices() {
  const [quoteService, setQuoteService] = useState<string | null>(null);

  return (
    <>
      <section className="py-20 bg-gradient-to-b from-white to-gray-50 dark:from-slate-900 dark:to-slate-800 transition-colors duration-300">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              <i className="ri-code-s-slash-line"></i>
              <span>Specialized Programming</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-gray-100 mb-4">
              Control System Programming
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              We bring your smart home to life with expert programming for the industry's most reliable control platforms
            </p>
          </div>

          <div className="space-y-12">
            {programmingServices.map((service, index) => (
              <div
                key={service.id}
                className={`bg-white dark:bg-slate-800 rounded-3xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } flex flex-col lg:flex group`}
              >
                <div className="lg:w-1/2 h-80 lg:h-auto w-full relative overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                </div>

                <div className="lg:w-1/2 p-8 lg:p-12 flex flex-col justify-center">
                  <div className="inline-flex items-center gap-2 bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 w-fit">
                    <i className={service.icon} />
                    <span>Programming Solution</span>
                  </div>

                  <h3 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-4">
                    {service.title}
                  </h3>

                  <p className="text-base text-gray-600 dark:text-gray-400 mb-8 leading-relaxed whitespace-pre-line">
                    {service.description}
                  </p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-line text-green-600 dark:text-green-400 text-sm"></i>
                        </div>
                        <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-wrap gap-2 mb-8">
                    {service.tags.map((tag, idx) => (
                      <span
                        key={idx}
                        className="text-[10px] uppercase tracking-wider bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400 px-3 py-1.5 rounded-lg font-bold"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto pt-8 border-t border-gray-100 dark:border-slate-700">
                    <button
                      onClick={() => setQuoteService(service.title)}
                      className="inline-flex items-center justify-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-8 py-3 rounded-xl font-bold transition-all shadow-lg shadow-violet-200 dark:shadow-none cursor-pointer"
                    >
                      Request Quote
                      <i className="ri-arrow-right-line" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RequestQuotePopup
        isOpen={!!quoteService}
        onClose={() => setQuoteService(null)}
        serviceName={quoteService || undefined}
      />
    </>
  );
}
